import { useQuery } from '@tanstack/react-query';
import { Fragment } from 'react';
import { PageContainer } from '../app/AppLayout';
import { useApi } from '../api/context';
import { queryKeys } from '../api/queries';
import type { ChangelogSection } from '../api/types';
import { useAuth } from '../auth/context';
import { ErrorMessage, Loading, PageHeader } from '../components/ui';
import { formatReleaseDate } from '../lib/format';
import { parseInline } from '../lib/inlineMarkdown';
import { strings } from '../strings';

const s = strings.whatsNew;

const anchor = (version: string) => version.replaceAll('.', '-');

/** An entry's text with its `code` and **strong** parts, as React text nodes only (spec 6.1.13). */
function EntryText({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((part, i) => (
        <Fragment key={i}>
          {part.kind === 'code' ? (
            <code className="whats-new-code">{part.text}</code>
          ) : part.kind === 'strong' ? (
            <strong>{part.text}</strong>
          ) : (
            part.text
          )}
        </Fragment>
      ))}
    </>
  );
}

function Release({
  id,
  title,
  date,
  current,
  help,
  sections,
}: {
  id: string;
  title: string;
  date?: string;
  current?: boolean;
  help?: string;
  sections: ChangelogSection[];
}) {
  const headingId = `release-${id}`;
  return (
    <section id={`version-${id}`} aria-labelledby={headingId} className="card whats-new-release">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id={headingId} className="text-base font-semibold">
          {title}
        </h2>
        {current && <span className="badge tone-info">{s.current}</span>}
        {date && (
          <time dateTime={date} className="text-xs text-fg-muted">
            {formatReleaseDate(date)}
          </time>
        )}
      </div>
      {help && <p className="help mt-1">{help}</p>}
      {sections.map((section) => (
        <div key={section.category} className="mt-3">
          <h3 className="whats-new-category">{s.category[section.category]}</h3>
          <ul className="whats-new-list">
            {section.entries.map((entry, i) => (
              <li key={i}>
                {entry.breaking && <span className="badge tone-danger mr-1.5">{s.breaking}</span>}
                <EntryText text={entry.text} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

/** `/whats-new` (spec 6.14): the changelog from `GET /version/changelog`, newest release first. */
export function WhatsNewPage() {
  const api = useApi();
  const { config } = useAuth();
  const changelog = useQuery({
    queryKey: queryKeys.changelog,
    queryFn: () => api.getChangelog(),
    staleTime: Infinity,
  });
  const data = changelog.data;
  return (
    <PageContainer>
      <PageHeader title={s.title}>
        <p className="help mt-1">{s.intro(config.version)}</p>
      </PageHeader>
      {changelog.error ? (
        <ErrorMessage error={changelog.error} onRetry={() => void changelog.refetch()} />
      ) : !data ? (
        <Loading />
      ) : (
        <div className="flex max-w-3xl flex-col gap-4">
          {data.releases.length > 0 && (
            <nav aria-label={s.versions} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="text-fg-muted">{s.jumpTo}</span>
              <ul className="flex flex-wrap gap-x-3 gap-y-1">
                {data.unreleased.sections.length > 0 && (
                  <li>
                    <a className="link" href="#version-unreleased">
                      {s.unreleased}
                    </a>
                  </li>
                )}
                {data.releases.map((release) => (
                  <li key={release.version}>
                    <a className="link" href={`#version-${anchor(release.version)}`}>
                      {release.version}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}
          {data.unreleased.sections.length > 0 && (
            <Release
              id="unreleased"
              title={s.unreleased}
              help={s.unreleasedHelp}
              sections={data.unreleased.sections}
            />
          )}
          {data.releases.map((release) => (
            <Release
              key={release.version}
              id={anchor(release.version)}
              title={s.release(release.version)}
              date={release.date}
              current={release.version === config.version}
              sections={release.sections}
            />
          ))}
          {data.releases.length === 0 && data.unreleased.sections.length === 0 && (
            <p className="help">{s.empty}</p>
          )}
        </div>
      )}
    </PageContainer>
  );
}
